import { useMessagesWithExptAndChoreoDisabled } from '@atlassian/post-office-context/narrow-exposure/use-messages-with-expt-and-choreo-disabled';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import {
	MaybeSuspenseBoundary,
	type MessageProps,
} from '@post-office/placement-common/utils/message-renderer';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import { memo, type ReactNode, useCallback, useEffect, useState } from 'react';

import { ConfluenceHomeBannerPlacementSkeleton } from './skeleton';

import { MessageRenderer, type renderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { type ConfluenceHomeBannerInitialData } from '../api/types';

import { type ConfluenceHomeBannerPlacement } from '../contracts/Placement';

import { type ErrorBoundariedProps } from '@post-office/placement-contexts/create-placement-component/types';

export type Message = MessageProps<typeof renderer>;

type BannerState =
	| { status: 'loading' }
	| { status: 'selecting'; messages: ConfluenceHomeBannerInitialData['messages'] }
	| { status: 'settled'; message?: ConfluenceHomeBannerInitialData['messages'][number] };

const excludedMessages = new Set(['test-message-inapp', 'message-template-template']);

/**
 * Confluence Home Banner does not have IntlProvider, all it's translations are managed by Confluence
 *
 * @see confluence/next/packages/i18n/src/messages.ts
 */
const ConfluenceHomeBanner: (
	props: ErrorBoundariedProps<ConfluenceHomeBannerPlacement>,
) => ReactNode = createPlacementComponent<ConfluenceHomeBannerPlacement>(
	PLACEMENT_ID,
	memo(({ initialData, preloadedInitialData }) => {
		const { data, isLoading } = useInitialData<ConfluenceHomeBannerInitialData>(initialData);
		const [state, setMessageState] = useState<BannerState>(() =>
			preloadedInitialData
				? { status: 'selecting', messages: preloadedInitialData.messages ?? [] }
				: { status: 'loading' },
		);

		const handleOnDismissed = useCallback(() => {
			setMessageState({ status: 'settled' });
		}, []);

		const [selectedMessage] = useMessagesWithExptAndChoreoDisabled(
			state.status === 'selecting' ? state.messages : [],
			{ maxMessagesToDisplay: 1 },
		);
		const message = state.status === 'settled' ? state.message : selectedMessage;

		useEffect(() => {
			if (state.status === 'loading' && !isLoading) {
				setMessageState({
					status: 'selecting',
					messages: (data?.messages ?? []).filter(
						(candidate) => !excludedMessages.has(candidate.messageTemplateId),
					),
				});
			} else if (state.status === 'selecting') {
				// Keep the response's decision, including an empty result or dismissal.
				setMessageState((previous) =>
					previous.status === 'selecting'
						? { status: 'settled', message: selectedMessage }
						: previous,
				);
			}
		}, [state.status, isLoading, data, selectedMessage]);

		if (state.status === 'loading') {
			return <ConfluenceHomeBannerPlacementSkeleton />;
		}

		return (
			<>
				{message && (
					// Adding @ts-ingore to suppress the error in confluence typecheck and below error is not thrown in post-office typecheck
					// This error exists because confluence is using react 16 and post-office is using react 18
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore - 'ReactNode | ReactElement<any, any>' is not a valid JSX element.
					<MessageRenderer
						{...message}
						id={message.messageInstanceId}
						messageInstanceId={message.messageInstanceId}
						onDismissed={handleOnDismissed}
						SuspenseBoundary={MaybeSuspenseBoundary}
					/>
				)}
			</>
		);
	}),
	{
		choreographer: {
			// Disabling this placement from being choreographed as it is not a pop-up
			isDefaultEnabled: false,
		},
	},
);

export default ConfluenceHomeBanner;
